import React, { useEffect, useState } from "react";
import SearchContainer from "./SearchContainer";
import SearchParams from "../../../types/searchParams";
import SettingsModal from "../../../types/settingsModal";
import { Base } from "../../base";
import { debounce } from "lodash";
import { setJobFilter } from "../../../slices/job";
import { useDispatch, useSelector } from "../../../store";
import ColumnDef from "../../../types/columnDef";
import JobType from "../../../types/jobType";
import { useNavigate } from "react-router-dom";
import {
  getJobTypeId,
  getJobTypes,
  reorderJobTypes,
} from "../../../slices/jobType";
import { Box, CircularProgress, Grid, IconButton } from "@mui/material";
import theme from "../../../theme";
import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragIndicator } from "@mui/icons-material";
import checkRole, { UserRoles } from "../../../helpers/checkRole";

interface Props {
  apiJobTypeSearch: (search: SearchParams) => Promise<any>;
  apiGetJobType: (id: number) => void;
  resetJobType: () => void;
  phone: boolean;
  setDeleteModal: React.Dispatch<React.SetStateAction<SettingsModal>>;
  setJobTypeFilter: (params: SearchParams) => void;
  deleteModal: SettingsModal;
}

const JobTypes: React.FC<Props> = (props) => {
  const {
    apiJobTypeSearch,
    apiGetJobType,
    resetJobType,
    phone,
    setDeleteModal,
    setJobTypeFilter,
    deleteModal,
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { jobTypeFilter, jobTypeSearchResults, searchStatus, status } =
    useSelector((store) => store.jobType);
  const { loggedInUser } = useSelector((store) => store.authentication);
  const [mobileRows, setMobileRows] = useState<any[]>([]);
  const [highlightId, setHighlightId] = useState(0);
  const [reorder, setReorder] = useState(false);
  const [jobTypeOrder, setJobTypeOrder] = useState<JobType[]>([]);

  useEffect(() => {
    dispatch(getJobTypes()).then((result: any) => {
      let types: JobType[] = [];
      result.payload.forEach((t: JobType, i: number) => {
        types.push({ ...t, order: t.order && t.order > 0 ? t.order : i + 1 });
      });
      setJobTypeOrder(types);
    });
  }, [dispatch]);

  useEffect(() => {
    if (
      !deleteModal.visible &&
      deleteModal.modal === undefined &&
      highlightId > 0
    ) {
      setHighlightId(0);
    }
  }, [deleteModal.modal, deleteModal.visible, highlightId]);

  const updateSearch = debounce((query: string) => {
    setJobFilter({
      ...jobTypeFilter,
      query,
      pageNumber: phone ? 2 : 1,
    });
    apiJobTypeSearch({
      ...jobTypeFilter,
      pageNumber: 1,
      query,
    }).then((result) => {
      setMobileRows(result);
    });
  }, 500);

  const columns: ColumnDef[] = [
    {
      field: "name",
      headerName: "Job Type",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    return array.map((jt: JobType) => {
      let rowObject = {
        id: jt.id,
        name: jt.name,
        actions: (
          <Base.TableActions
            id={jt.id}
            handleEdit={(id) => {
              dispatch(getJobTypeId(id)).then(({ meta }) => {
                if (meta.requestStatus === "fulfilled") {
                  navigate(`/job-type/${id}`);
                }
              });
            }}
            handleDelete={(id) => {
              apiGetJobType(id);
              setDeleteModal({ visible: true, modal: "jobType" });
              setHighlightId(id);
            }}
          />
        ),
      };
      return rowObject;
    });
  };

  const changePage = (pageNumber?: number, pageSize?: number) => {
    apiJobTypeSearch({ ...jobTypeFilter, pageNumber, pageSize }).then(() => {
      setJobFilter({ ...jobTypeFilter, pageNumber, pageSize });
    });
  };

  const next = () => {
    apiJobTypeSearch(jobTypeFilter).then((result: any) => {
      setJobTypeFilter({
        ...jobTypeFilter,
        pageNumber: jobTypeFilter?.pageNumber! + 1,
      });
      setMobileRows([...mobileRows, ...result]);
    });
  };

  const SortableItem: React.FC<{ type: JobType; children: React.ReactNode }> = (
    props
  ) => {
    const { type, children } = props;
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      setActivatorNodeRef,
    } = useSortable({
      id: type.order!,
      data: type,
      animateLayoutChanges: () => false,
    });

    return (
      <div
        ref={setNodeRef}
        style={{
          transform: CSS.Translate.toString(transform),
          transition: transition,
          width: "100%",
        }}
      >
        <IconButton {...attributes} {...listeners} ref={setActivatorNodeRef}>
          <DragIndicator />
        </IconButton>
        {children}
      </div>
    );
  };

  return (
    <>
      <SearchContainer
        updateSearch={updateSearch}
        searchValue={jobTypeFilter.query}
        modalOpen={() => navigate(`/job-type/0`)}
        reset={resetJobType}
        extraFunction={
          jobTypeOrder.length >= 2 &&
          checkRole(loggedInUser!, UserRoles.GlobalAdmin)
            ? { text: "Reorder", function: () => setReorder(true) }
            : undefined
        }
      >
        <Base.TableContainer
          columns={columns}
          rows={displayRows(jobTypeSearchResults?.results || [])}
          mobileRows={displayRows(mobileRows)}
          page={jobTypeFilter.pageNumber}
          pageSize={jobTypeFilter.pageSize}
          totalRows={jobTypeSearchResults?.totalResults}
          changePage={changePage}
          next={next}
          highlightId={highlightId}
        />
      </SearchContainer>
      <Base.Modal open={reorder} onClose={() => setReorder(false)}>
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            width: 300,
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          {searchStatus !== "loading" ? (
            <Grid container direction={"row"} justifyContent={"space-between"}>
              <Grid size={12}>
                <Grid container direction={"row"}>
                  <DndContext
                    onDragEnd={(e: DragEndEvent) => {
                      const { over, active } = e;
                      if (!over) return;

                      const oldIndex = jobTypeOrder.indexOf(
                        jobTypeOrder.find((j) => j.order === active.id)!
                      );
                      const newIndex = jobTypeOrder.indexOf(
                        jobTypeOrder.find((j) => j.order === over.id)!
                      );

                      const move = (from: number, to: number) => {
                        setJobTypeOrder((items) => {
                          const updated = [...items];
                          const [movedItem] = updated.splice(from, 1);
                          updated.splice(to, 0, movedItem);
                          return updated;
                        });
                      };

                      move(oldIndex, newIndex);
                    }}
                    collisionDetection={closestCenter}
                  >
                    <ul style={{ width: "100%", margin: 0, padding: 0 }}>
                      <Grid
                        container
                        flex={1}
                        alignContent={"flex-start"}
                        sx={{ height: "350px", overflow: "auto" }}
                      >
                        <SortableContext
                          items={jobTypeOrder.map((j) => j.order!)}
                          strategy={verticalListSortingStrategy}
                        >
                          {jobTypeOrder.map((j) => (
                            <SortableItem type={j} key={j.id}>
                              {j.name}
                            </SortableItem>
                          ))}
                        </SortableContext>
                      </Grid>
                    </ul>
                  </DndContext>
                </Grid>
              </Grid>
              <Grid>
                <Base.Button
                  text="Cancel"
                  type="secondary"
                  onClick={() => setReorder(false)}
                />
              </Grid>
              <Grid>
                <Base.Button
                  text="Update"
                  onClick={() => {
                    const ordered = jobTypeOrder.map((j, i) => {
                      return { ...j, order: i + 1 };
                    });
                    dispatch(reorderJobTypes(ordered)).then((result) => {
                      if (result.meta.requestStatus === "fulfilled") {
                        updateSearch("");
                        setReorder(false);
                      }
                    });
                  }}
                  disabled={status === "loading"}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid direction={"row"} justifyContent={"center"} flex={1}>
              <Base.Text text="Loading, please wait..." variant="h5" center />
              <CircularProgress />
            </Grid>
          )}
        </Box>
      </Base.Modal>
    </>
  );
};

export default JobTypes;
